import { useEffect, useState } from 'react';
import { computeReceiptHash, MandateSchema, type ActionReceipt, type Mandate, type Profile, type ProposedAction } from './domain';
import type { ViewId } from './demo-data';

export const APP_STATE_KEY = 'personal-agency-os.state.v1';
export const APP_STATE_VERSION = 1 as const;

export type AppState = {
  version: typeof APP_STATE_VERSION;
  ownerName: string;
  onboardingComplete: boolean;
  profile: Profile;
  activeView: ViewId;
  mandates: Record<Profile, Mandate>;
  receipts: ActionReceipt[];
  customActions: Record<Profile, ProposedAction[]>;
  updatedAt: string;
};

export function parseAppState(input: unknown, fallback: AppState): AppState {
  if (!input || typeof input !== 'object') return fallback;
  const candidate = input as Partial<AppState>;
  if (candidate.version !== APP_STATE_VERSION) return fallback;

  try {
    return {
      ...fallback,
      ...candidate,
      ownerName: typeof candidate.ownerName === 'string' && candidate.ownerName.trim() ? candidate.ownerName : fallback.ownerName,
      onboardingComplete: Boolean(candidate.onboardingComplete),
      profile: candidate.profile === 'personal' ? 'personal' : 'professional',
      activeView: candidate.activeView ?? fallback.activeView,
      mandates: {
        personal: MandateSchema.parse(candidate.mandates?.personal ?? fallback.mandates.personal),
        professional: MandateSchema.parse(candidate.mandates?.professional ?? fallback.mandates.professional),
      },
      receipts: Array.isArray(candidate.receipts) ? candidate.receipts : fallback.receipts,
      customActions: {
        personal: Array.isArray(candidate.customActions?.personal) ? candidate.customActions.personal : [],
        professional: Array.isArray(candidate.customActions?.professional) ? candidate.customActions.professional : [],
      },
      updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : new Date().toISOString(),
    };
  } catch {
    return fallback;
  }
}

export function usePersistentAppState(initialState: AppState) {
  const [state, setState] = useState<AppState>(() => {
    if (typeof window === 'undefined') return initialState;
    try {
      const stored = window.localStorage.getItem(APP_STATE_KEY);
      return stored ? parseAppState(JSON.parse(stored), initialState) : initialState;
    } catch {
      return initialState;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(APP_STATE_KEY, JSON.stringify({ ...state, updatedAt: new Date().toISOString() }));
  }, [state]);

  return [state, setState] as const;
}

export function downloadJson(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function readJsonFile(file: File): Promise<unknown> {
  return JSON.parse(await file.text());
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

async function deriveKey(passphrase: string, salt: Uint8Array) {
  const material = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(new TextEncoder().encode(passphrase)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: toArrayBuffer(salt), iterations: 210_000, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export type EncryptedBackup = {
  format: 'personal-agency-os.encrypted-backup';
  version: 1;
  salt: string;
  iv: string;
  ciphertext: string;
};

export async function encryptBackup(state: AppState, passphrase: string): Promise<EncryptedBackup> {
  if (passphrase.length < 8) throw new Error('Passphrase must contain at least 8 characters.');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(new TextEncoder().encode(JSON.stringify(state))),
  );
  return {
    format: 'personal-agency-os.encrypted-backup',
    version: 1,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

export async function decryptBackup(backup: EncryptedBackup, passphrase: string): Promise<unknown> {
  if (backup.format !== 'personal-agency-os.encrypted-backup') throw new Error('Unsupported backup format.');
  const salt = base64ToBytes(backup.salt);
  const iv = base64ToBytes(backup.iv);
  const key = await deriveKey(passphrase, salt);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(base64ToBytes(backup.ciphertext)),
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
}

export type ReceiptChainValidation = {
  valid: boolean;
  brokenAt?: string;
  reason?: 'PREVIOUS_HASH_MISMATCH' | 'RECEIPT_HASH_MISMATCH';
};

export function validateReceiptChain(receipts: ActionReceipt[]): ReceiptChainValidation {
  for (let index = 0; index < receipts.length; index += 1) {
    const receipt = receipts[index];
    const expectedPrevious = receipts[index + 1]?.receiptHash ?? 'GENESIS';
    if (receipt.previousHash !== expectedPrevious) {
      return { valid: false, brokenAt: receipt.id, reason: 'PREVIOUS_HASH_MISMATCH' };
    }
    if (receipt.receiptHash !== computeReceiptHash(receipt)) {
      return { valid: false, brokenAt: receipt.id, reason: 'RECEIPT_HASH_MISMATCH' };
    }
  }
  return { valid: true };
}
